import { useParams } from 'react-router-dom';
import { useReservation, useDeletedReservation, useUsers } from '@/hooks/api-hooks';
import ReservationEditPage from './edit-reservation-page';
import ReservationViewPage from './reservation-view-page';
import { Loader, Alert, Badge } from '@mantine/core';
import { localTenant, localUser } from '@/services/session-service';
import { NotFound } from '@/components/not-found';
import { useTranslation } from 'react-i18next';
import { IconTrash, IconInfoCircle } from '@tabler/icons-react';
import { canUserEditReservation } from '@/lib/edition-groups-utils';

export default function ReservationPage() {
    const { id } = useParams<{ id: string }>();
    const tenant = localTenant();
    const currentUser = localUser();
    const { t } = useTranslation();

    const reservationId = Number(id);

    const {
        data: resData,
        isLoading: isResLoading,
    } = useReservation(reservationId);

    const {
        data: deletedResData,
        isLoading: isDeletedLoading,
    } = useDeletedReservation(id, {
        enabled: !isResLoading && !resData?.data && !!id,
    });

    const { data: usersResponse } = useUsers();

    if (isResLoading || (isDeletedLoading && !resData?.data)) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader color="black" />
            </div>
        );
    }

    const reservation = resData?.data;
    const deletedRecord = deletedResData?.data;

    if (!reservation && deletedRecord?.data) {
        const deletedByUser = deletedRecord.deleted_by?.name || deletedRecord.deleted_by?.email || t('user');
        const deletedDateStr = new Date(deletedRecord.deleted_at).toLocaleString();

        return (
            <div className="space-y-4">
                <Alert
                    icon={<IconTrash size={20} />}
                    title={
                        <div className="flex items-center gap-2">
                            <Badge color="red" variant="filled">{t('deleted_reservation')}</Badge>
                            <span>#{deletedRecord.reservation_id}</span>
                        </div>
                    }
                    color="red"
                    variant="light"
                    className="border border-red-200 shadow-xs"
                >
                    <p className="text-sm font-medium text-red-800 mt-1">
                        {t('deleted_reservation_banner', { date: deletedDateStr, user: deletedByUser })}
                    </p>
                </Alert>
                <ReservationViewPage reservation={deletedRecord.data} />
            </div>
        );
    }

    if (!reservation) {
        return <NotFound type="reservation" />;
    }

    const users = usersResponse?.data || [];
    const creatorUser = users.find((u: any) => Number(u.id) === Number(reservation.created_by?.id));

    const isTenantMatch = reservation.tenant_id === tenant?.id;
    const canEdit = isTenantMatch && canUserEditReservation(currentUser, reservation, tenant?.id, creatorUser);

    return <ReservationEditPage
        reservation={reservation}
        disabled={!canEdit}
    />
}
